// src/lib/opportunity/proposalContactResolver.ts

import type { AuthProfile } from "@/hooks/authStateModel";
import type { RepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import type { OpportunityFormatKey } from "../../types/opportunity.js";
import { FORMAT_CALC_PRODUCT_TYPE_WILDCARD } from "./formatCalcLineTypes.js";
import { getFormatProposalContact } from "./formatProposalContactClient.js";
import {
  mergeProposalContact,
  orgRowToProposalContact,
  selectFormatProposalContact,
  type FormatProposalContact,
  type OrgProposalContactRow,
  type ProposalContactInfo,
} from "./proposalContactTypes.js";
import { repProposalContactOverride } from "./repProposalContact.js";

export type ResolvedProposalContact = {
  contact: ProposalContactInfo;
  allowRepContactOverride: boolean;
};

export async function resolveFormatProposalContactSettings(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}): Promise<{ formatContact: FormatProposalContact | null; allowRepContactOverride: boolean }> {
  const [specific, wildcard] = await Promise.all([
    getFormatProposalContact({ format_key: args.formatKey, product_type: args.productType }),
    getFormatProposalContact({
      format_key: args.formatKey,
      product_type: FORMAT_CALC_PRODUCT_TYPE_WILDCARD,
    }),
  ]);
  const rows: FormatProposalContact[] = [];
  if (specific.ok && specific.data.contact) rows.push(specific.data.contact);
  if (wildcard.ok && wildcard.data.contact) rows.push(wildcard.data.contact);
  const def = selectFormatProposalContact({ rows, productType: args.productType });
  return {
    formatContact: rows.find((r) => r.product_type === args.productType) ??
      rows.find((r) => r.product_type === FORMAT_CALC_PRODUCT_TYPE_WILDCARD) ??
      null,
    allowRepContactOverride: def?.allow_rep_contact_override ?? false,
  };
}

export function resolveProposalContact(args: {
  orgRow: OrgProposalContactRow;
  brandNameFallback: string;
  organizationNameFallback?: string;
  formatOverride: Partial<FormatProposalContact> | null | undefined;
  useRepContact: boolean;
  allowRepContactOverride: boolean;
  profile: AuthProfile | null | undefined;
  location: RepContactLocation | null | undefined;
}): ResolvedProposalContact {
  const orgBase = orgRowToProposalContact(
    args.orgRow,
    args.brandNameFallback,
    args.organizationNameFallback,
  );
  const merged = mergeProposalContact(orgBase, args.formatOverride);
  const allowRep = args.allowRepContactOverride;
  if (allowRep && args.useRepContact) {
    return {
      contact: repProposalContactOverride({
        base: merged,
        profile: args.profile,
        location: args.location,
      }),
      allowRepContactOverride: allowRep,
    };
  }
  return { contact: merged, allowRepContactOverride: allowRep };
}
